import React, { useState } from "react";
import { X, History, Calendar, Code, Check, Clock, FileText, User } from "lucide-react";

const HistoryModal = ({
  t,
  styles,
  theme,
  isRTL,
  language,
  actionHistory,
  ACTION_TYPES,
  hasFeatureAccess,
  onClose,
}) => {
  const [detailsAction, setDetailsAction] = useState(null);
  // Admin-level feature gating (registry "changesPage.viewactiondetails") —
  // a promoted user without this feature can still see the history table but
  // can't drill into a row's full details. Bypassed roles / non-admin-view
  // sessions always pass, same as every other hasFeatureAccess check.
  const canViewDetails = !hasFeatureAccess || hasFeatureAccess("changesPage", "viewactiondetails");

  return (
    <div style={styles.modal} onClick={onClose}>
      <div
        style={{ ...styles.modalContent, maxWidth: "min(95vw, 1200px)" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={styles.modalHeader}>
          <div
            style={{
              display: "flex",
              flexDirection: isRTL ? "row" : "row",
              alignItems: "center",
            }}
          >
            <div
              style={{
                ...styles.modalIconWrapper,
                background: "linear-gradient(135deg, #06b6d4, #0891b2)",
              }}
            >
              <History size={26} color="white" />
            </div>
            <h2 style={styles.modalTitle}>{t.actionHistory}</h2>
          </div>
          <button
            style={styles.closeButton}
            onClick={onClose}
            className="enhanced-button-hover"
          >
            <X size={24} />
          </button>
        </div>

        <div style={styles.tableWrapper}>
          {actionHistory.length > 0 ? (
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>{t.date}</th>
                  <th style={styles.th}>{t.employee}</th>
                  <th style={styles.th}>{t.actionType}</th>
                  <th style={styles.th}>{t.status}</th>
                  <th style={styles.th}>{t.details}</th>
                </tr>
              </thead>
              <tbody>
                {actionHistory.map((action) => {
                  const actionConfig = ACTION_TYPES.find(
                    (a) => a.id === action.actionType
                  );
                  const ActionIcon = actionConfig?.icon || FileText;

                  return (
                    <tr
                      key={action.id}
                      className="table-row-hover"
                      onClick={canViewDetails ? () => setDetailsAction(action) : undefined}
                      style={{ cursor: canViewDetails ? "pointer" : "default" }}
                      title={canViewDetails ? (isRTL ? "اضغط لعرض التفاصيل" : "Click to see details") : undefined}
                    >
                      <td style={styles.td}>
                        <div
                          style={{
                            display: "flex",
                            flexDirection: isRTL ? "row" : "row",
                            alignItems: "center",
                            gap: "8px",
                          }}
                        >
                          <Calendar size={16} />
                          {new Date(
                            action.effectiveDate ||
                              action.createdAt ||
                              action.requestedDate
                          ).toLocaleDateString(
                            language === "ar" ? "ar-EG" : "en-US"
                          )}
                        </div>
                      </td>
                      <td style={styles.td}>
                        <div style={styles.employeeInfo}>
                          <div style={styles.employeeName}>
                            {language === "ar"
                              ? action.employeeArabicName ||
                                action.employeeName ||
                                t.notAvailable
                              : action.employeeName ||
                                action.employeeArabicName ||
                                t.notAvailable}
                          </div>
                          <div style={styles.employeeDetail}>
                            <Code size={13} />
                            {action.clientCode || action.employee?.clientCode || action.infinityCode || action.employeeId}
                          </div>
                        </div>
                      </td>
                      <td style={styles.td}>
                        <div
                          style={{
                            ...styles.badge,
                            background:
                              actionConfig?.bgColor ||
                              "rgba(100, 116, 139, 0.15)",
                            border: `1px solid ${
                              actionConfig?.borderColor ||
                              "rgba(100, 116, 139, 0.4)"
                            }`,
                            color: theme === "dark" ? "#ffffff" : "#0f172a",
                          }}
                        >
                          <ActionIcon size={14} />
                          {actionConfig
                            ? t[actionConfig.labelKey]
                            : action.actionType}
                        </div>
                      </td>
                      <td style={styles.td}>
                        <div
                          style={{
                            ...styles.badge,
                            background:
                              action.status === "approved" ||
                              action.status === "completed"
                                ? "rgba(16, 185, 129, 0.15)"
                                : action.status === "pending"
                                ? "rgba(245, 158, 11, 0.15)"
                                : "rgba(239, 68, 68, 0.15)",
                            border:
                              action.status === "approved" ||
                              action.status === "completed"
                                ? "1px solid rgba(16, 185, 129, 0.4)"
                                : action.status === "pending"
                                ? "1px solid rgba(245, 158, 11, 0.4)"
                                : "1px solid rgba(239, 68, 68, 0.4)",
                            color:
                              action.status === "approved" ||
                              action.status === "completed"
                                ? theme === "dark"
                                  ? "#34d399"
                                  : "#059669"
                                : action.status === "pending"
                                ? theme === "dark"
                                  ? "#fbbf24"
                                  : "#d97706"
                                : theme === "dark"
                                ? "#f87171"
                                : "#dc2626",
                          }}
                        >
                          {action.status === "approved" ||
                          action.status === "completed" ? (
                            <Check size={14} />
                          ) : action.status === "pending" ? (
                            <Clock size={14} />
                          ) : (
                            <X size={14} />
                          )}
                          {t[action.status] || action.status || t.unknown}
                        </div>
                      </td>
                      <td style={styles.td}>
                        <div
                          style={{
                            fontSize: "clamp(11px, 1.6vw, 13px)",
                            color:
                              theme === "dark"
                                ? "rgba(255,255,255,0.65)"
                                : "#64748b",
                            maxWidth: "300px",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            textAlign: isRTL ? "right" : "left",
                          }}
                        >
                          {action.reason || t.noDetailsProvided}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          ) : (
            <div style={styles.emptyState}>
              <div style={styles.emptyStateIcon}>
                <History size={80} />
              </div>
              <h3 style={styles.emptyStateTitle}>{t.noActionHistory}</h3>
              <p style={styles.emptyStateText}>{t.noActionsRecorded}</p>
            </div>
          )}
        </div>
      </div>

      {detailsAction && canViewDetails && (() => {
        const actionConfig = ACTION_TYPES.find((a) => a.id === detailsAction.actionType);
        const ActionIcon = actionConfig?.icon || FileText;
        const employeeDisplayName =
          (language === "ar"
            ? detailsAction.employeeArabicName || detailsAction.employeeName
            : detailsAction.employeeName || detailsAction.employeeArabicName) || t.notAvailable;
        const row = (label, value) => (
          <div style={{ ...styles.infoRow }}>
            <span style={styles.infoLabel}>{label}</span>
            <span style={styles.infoValue}>{value ?? t.notAvailable}</span>
          </div>
        );

        // Every action type (promote/demote/transfer/terminate/suspend/...)
        // stores different extra fields (newPosition, newSalary, terminationReason,
        // suspensionType, rotationDuration, ...) — rather than hardcoding a
        // field list per type here (and having to remember to update it every
        // time a type gains a field), show whatever the record actually has.
        // Already-displayed keys are excluded so nothing repeats.
        const ALREADY_SHOWN = new Set([
          "id", "actionType", "employeeId", "infinityCode", "employeeName",
          "employeeArabicName", "status", "reason", "effectiveDate",
          "createdAt", "requestedDate", "approvedBy", "rejectionReason",
          "updatedAt", "__typename", "employee",
        ]);
        const humanize = (key) =>
          key.replace(/([A-Z])/g, " $1").replace(/^./, (s) => s.toUpperCase()).trim();
        const formatValue = (value) => {
          if (value === null || value === undefined || value === "") return null;
          if (typeof value === "boolean") return value ? (t.yes || "Yes") : (t.no || "No");
          if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}/.test(value)) {
            const d = new Date(value);
            if (!Number.isNaN(d.getTime())) return d.toLocaleDateString(isRTL ? "ar-EG" : "en-US");
          }
          if (typeof value === "object") return null; // nested objects aren't user-facing details
          return String(value);
        };
        const extraFields = Object.entries(detailsAction)
          .filter(([key]) => !ALREADY_SHOWN.has(key))
          .map(([key, value]) => [key, formatValue(value)])
          .filter(([, value]) => value !== null);

        return (
          <div style={styles.modal} onClick={() => setDetailsAction(null)}>
            <div
              style={{ ...styles.modalContent, maxWidth: "min(90vw, 600px)", maxHeight: "80vh", overflowY: "auto" }}
              onClick={(e) => e.stopPropagation()}
            >
              <div style={styles.modalHeader}>
                <div style={{ display: "flex", alignItems: "center" }}>
                  <div style={{ ...styles.modalIconWrapper, background: actionConfig?.color || "#2e7cba" }}>
                    <ActionIcon size={26} color="white" />
                  </div>
                  <h2 style={styles.modalTitle}>{t.actionDetails || "Action Details"}</h2>
                </div>
                <button style={styles.closeButton} onClick={() => setDetailsAction(null)}>
                  <X size={24} />
                </button>
              </div>

              <div style={styles.infoBox}>
                {row(
                  t.actionType,
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                    <ActionIcon size={14} />
                    {actionConfig ? t[actionConfig.labelKey] : detailsAction.actionType}
                  </span>
                )}
                {row(
                  t.employee,
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                    <User size={14} />
                    {employeeDisplayName}
                  </span>
                )}
                {row(t.infinityCode, detailsAction.clientCode || detailsAction.employee?.clientCode || detailsAction.infinityCode || detailsAction.employeeId)}
                {row(
                  t.date,
                  new Date(
                    detailsAction.effectiveDate || detailsAction.createdAt || detailsAction.requestedDate
                  ).toLocaleDateString(language === "ar" ? "ar-EG" : "en-US")
                )}
                {row(
                  t.status,
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                    {detailsAction.status === "approved" || detailsAction.status === "completed" ? (
                      <Check size={14} />
                    ) : detailsAction.status === "pending" ? (
                      <Clock size={14} />
                    ) : (
                      <X size={14} />
                    )}
                    {t[detailsAction.status] || detailsAction.status || t.unknown}
                  </span>
                )}
                {detailsAction.approvedBy && row(t.approvedBy || "Approved By", detailsAction.approvedBy)}
                {detailsAction.rejectionReason && row(t.rejectionReason || "Rejection Reason", detailsAction.rejectionReason)}

                {/* Every other field this specific action type recorded
                    (newPosition, newSalary, terminationReason, suspensionType,
                    rotationDuration, ...) — whatever exists on the record. */}
                {extraFields.map(([key, value]) => (
                  <React.Fragment key={key}>
                    {row(t[key] || humanize(key), value)}
                  </React.Fragment>
                ))}

                <div style={{ ...styles.infoRow, borderBottom: "none", marginBottom: 0, paddingBottom: 0, flexDirection: "column", alignItems: isRTL ? "flex-end" : "flex-start" }}>
                  <span style={styles.infoLabel}>{t.reason || t.details}</span>
                  <span style={{ ...styles.infoValue, marginTop: 6, whiteSpace: "normal", wordBreak: "break-word" }}>
                    {detailsAction.reason || t.noDetailsProvided}
                  </span>
                </div>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
};

export default HistoryModal;